import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box, Stack, Typography, Modal, CardMedia, IconButton } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useState, useEffect } from 'react';
import RejectionFiles from './RejectionFiles';
import { FormattedMessage } from 'react-intl';
import { EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import CancelIcon from '@mui/icons-material/Cancel';

const DropzoneWrapper = styled('div')(({ theme }) => ({
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: theme.palette.background.paper,
  border: `1px dashed ${theme.palette.secondary.main}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1),
  '&:hover': { opacity: 0.72, cursor: 'pointer' }
}));

const SingleFileUpload = ({
  error,
  file,
  setFieldValue,
  sx,
  onUpload,
  onRemoveCallBack,
  inputLabel
}) => {
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('Upload');
  const [uploadStatus, setUploadStatus] = useState('Upload');
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections
  } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: (acceptedFiles) => {
      const acceptedFile = acceptedFiles[0];
      setFieldValue(
        'file',
        Object.assign(acceptedFile, {
          preview: URL.createObjectURL(acceptedFile)
        })
      );
      setUploadStatus('Uploading...');
      if (onUpload) {
        onUpload(acceptedFiles);
      }
    }
  });

  useEffect(() => {
    if (file) {
      const newFileName = typeof file === 'string' ? file.split('/').pop() : file.name;
      setFileName(newFileName);
      setUploadStatus(newFileName);
    } else {
      setFileName('Upload');
      setUploadStatus('Upload');
    }
  }, [file]);

  const handleOpen = () => {
    setOpen(true);
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleClose = () => {
    setOpen(false);
    setPosition({ x: 0, y: 0 });
  };

  const onRemove = () => {
    setFieldValue('file', null);
    onRemoveCallBack(file);
    setUploadStatus('Upload');
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.2));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  // Pan handlers
  const handleMouseDown = (e) => {
    setDragging(true);
    setStartDrag({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPosition({
      x: e.clientX - startDrag.x,
      y: e.clientY - startDrag.y
    });
  };

  const handleMouseUp = () => setDragging(false);
  const handleMouseLeave = () => setDragging(false);

  return (
    <Box sx={{ width: '100%', height: '50%', ...sx }}>
      {!!inputLabel && (
        <Typography variant="subtitle1" gutterBottom>
          <FormattedMessage id={inputLabel} />
        </Typography>
      )}

      <DropzoneWrapper
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter'
          })
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body1">{uploadStatus}</Typography>
      </DropzoneWrapper>

      {file && (
        <Stack direction="row" alignItems="center" spacing={1} mt={1}>
          <IconButton color="success" onClick={handleOpen}>
            <EyeOutlined />
          </IconButton>
          <IconButton color="error" onClick={onRemove}>
            <DeleteOutlined />
          </IconButton>
        </Stack>
      )}

      {fileRejections.length > 0 && <RejectionFiles fileRejections={fileRejections} />}

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
            width: '60%',
            height: '60%',
            bgcolor: 'background.paper',
            p: 2,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
            <IconButton onClick={handleZoomIn}>
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={handleZoomOut}>
              <ZoomOutIcon />
            </IconButton>
            <IconButton onClick={handleRotate}>
              <RotateRightIcon />
            </IconButton>
            <IconButton onClick={handleClose}>
              <CancelIcon />
            </IconButton>
          </Stack>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CardMedia
              component="img"
              src={file?.preview || file}
              alt={fileName}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              sx={{
                cursor: dragging ? 'grabbing' : 'grab',
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})
                 rotate(${rotation}deg)`,
                transition: dragging ? 'none' : 'transform 0.1s ease',
                maxWidth: 'unset',
                maxHeight: 'unset',
                userSelect: 'none'
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

SingleFileUpload.propTypes = {
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  setFieldValue: PropTypes.func.isRequired,
  onRemoveCallBack: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  sx: PropTypes.object,
  inputLabel: PropTypes.string
};

export default SingleFileUpload;
